"use client";

/**
 * AnnouncementBar — dismissible private-access strip that sits ABOVE the
 * sticky SiteNav (rendered first in the root layout). Adapted from the
 * "Announcement + bar" /design variation. Uses `bg-foreground text-background`
 * so it inverts correctly in both the paper-light and pure-black themes.
 *
 * Dismissal persists via localStorage. Per the project hydration rule we never
 * read localStorage in a useState initializer — the bar renders by default
 * (SSR + first client render agree), then collapses on mount if previously
 * dismissed.
 */

import { useEffect, useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "motion/react";
import { ArrowRight, X } from "lucide-react";

const DISMISS_KEY = "AskFinz_announce_dismissed";

export function AnnouncementBar() {
  const [open, setOpen] = useState(true);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === "1") setOpen(false);
    } catch {
      /* storage blocked — keep showing */
    }
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="overflow-hidden bg-foreground text-background"
        >
          <div className="container-x relative flex items-center justify-center gap-3 px-6 py-2.5">
            <Link
              href="/beta"
              className="group inline-flex items-center gap-2 text-xs font-medium sm:text-sm"
            >
              <span className="dot-live shrink-0" />
              <span>
                Private access is opening up —{" "}
                <span className="font-serif italic">request an invite</span>
              </span>
              <ArrowRight
                className="size-3.5 transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
            <button
              type="button"
              aria-label="Dismiss announcement"
              onClick={dismiss}
              className="absolute right-4 flex size-6 items-center justify-center rounded-full text-background/70 transition-colors hover:bg-background/10 hover:text-background"
            >
              <X className="size-4" aria-hidden />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
